import { createBigDecimal } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { Chain } from '../../../../chain'
import type { LowerHex } from '../../../../types'
import type { TransactionDecoded } from '../../../../types/transactionDecoded'
import { createEvmAddressId } from '../../../../utils'
import { createDirection } from './createDirection'

type CreateTokenTransfersParameters = {
  chain: Chain
  transaction: TransactionDecoded
  relatedAddresses: Set<LowerHex>
}

export const createTokenTransfers = ({
  chain,
  transaction,
  relatedAddresses,
}: CreateTokenTransfersParameters): readonly Transfer[] => {
  const transfers: Transfer[] = []

  // value
  if (transaction.value > 0n) {
    if (
      relatedAddresses.has(transaction.from.address) ||
      (transaction.to !== undefined &&
        relatedAddresses.has(transaction.to.address))
    ) {
      transfers.push({
        from: createEvmAddressId({
          chainId: transaction.chainId,
          address: transaction.from.address,
        }),
        to:
          transaction.to === undefined
            ? undefined
            : createEvmAddressId({
                chainId: transaction.chainId,
                address: transaction.to.address,
              }),
        price: undefined,
        asset: chain.nativeToken.currency,
        amount: createBigDecimal(transaction.value, chain.nativeToken.decimals),
        direction: createDirection({
          from: transaction.from.address,
          to: transaction.to?.address,
          relatedAddresses,
        }),
      })
    }
  }

  // internal transactions
  for (const internalTransaction of transaction.internalTransactions) {
    if (internalTransaction.value <= 0n) {
      continue
    }
    if (
      !relatedAddresses.has(internalTransaction.from) &&
      (internalTransaction.to === undefined ||
        !relatedAddresses.has(internalTransaction.to))
    ) {
      continue
    }
    transfers.push({
      from: createEvmAddressId({
        chainId: transaction.chainId,
        address: internalTransaction.from,
      }),
      to:
        internalTransaction.to === undefined
          ? undefined
          : createEvmAddressId({
              chainId: transaction.chainId,
              address: internalTransaction.to,
            }),
      price: undefined,
      asset: chain.nativeToken.currency,
      amount: createBigDecimal(
        internalTransaction.value,
        chain.nativeToken.decimals,
      ),
      direction: createDirection({
        from: internalTransaction.from,
        to: internalTransaction.to,
        relatedAddresses,
      }),
    })
  }

  // erc20 transfers
  for (const erc20Transfer of transaction.erc20Transfers) {
    if (erc20Transfer.value === 0n) {
      continue
    }
    if (
      !relatedAddresses.has(erc20Transfer.from) &&
      !relatedAddresses.has(erc20Transfer.to)
    ) {
      continue
    }
    transfers.push({
      from: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc20Transfer.from,
      }),
      to: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc20Transfer.to,
      }),
      price: undefined,
      asset: erc20Transfer.token.currency,
      amount: createBigDecimal(
        erc20Transfer.value,
        erc20Transfer.token.decimals,
      ),
      direction: createDirection({
        from: erc20Transfer.from,
        to: erc20Transfer.to,
        relatedAddresses,
      }),
    })
  }

  // erc721 transfers
  for (const erc721Transfer of transaction.erc721Transfers) {
    if (
      !relatedAddresses.has(erc721Transfer.from) &&
      !relatedAddresses.has(erc721Transfer.to)
    ) {
      continue
    }
    transfers.push({
      from: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc721Transfer.from,
      }),
      to: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc721Transfer.to,
      }),
      price: undefined,
      asset: erc721Transfer.token,
      amount: createBigDecimal(1n),
      direction: createDirection({
        from: erc721Transfer.from,
        to: erc721Transfer.to,
        relatedAddresses,
      }),
    })
  }

  // erc1155 transfers
  for (const erc1155Transfer of transaction.erc1155Transfers) {
    if (erc1155Transfer.value === 0n) {
      continue
    }
    if (
      !relatedAddresses.has(erc1155Transfer.from) &&
      !relatedAddresses.has(erc1155Transfer.to)
    ) {
      continue
    }
    transfers.push({
      from: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc1155Transfer.from,
      }),
      to: createEvmAddressId({
        chainId: transaction.chainId,
        address: erc1155Transfer.to,
      }),
      price: undefined,
      asset: erc1155Transfer.token,
      amount: createBigDecimal(erc1155Transfer.value),
      direction: createDirection({
        from: erc1155Transfer.from,
        to: erc1155Transfer.to,
        relatedAddresses,
      }),
    })
  }

  return transfers
}
